import { useState, useCallback } from 'react';
import { validateImageFile } from '../components/aesthetic/ImageUpload';
import { readFileAsDataUrl } from '../utils/imageUtils';
import { analyzeNose } from '../services/aestheticApi';

export function useNoseAnalysis() {
  const [beforeImage, setBeforeImage] = useState(null);
  const [afterImage, setAfterImage] = useState(null);
  const [beforePreview, setBeforePreview] = useState(null);
  const [afterPreview, setAfterPreview] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const handleImageChange = useCallback(async (field, file) => {
    setFieldErrors((prev) => ({ ...prev, [field]: null }));
    setError('');
    setResults(null);

    if (!file) {
      if (field === 'before') {
        setBeforeImage(null);
        setBeforePreview(null);
      } else {
        setAfterImage(null);
        setAfterPreview(null);
      }
      return;
    }

    const validationError = validateImageFile(file);
    if (validationError) {
      setFieldErrors((prev) => ({ ...prev, [field]: validationError }));
      return;
    }

    const preview = await readFileAsDataUrl(file);
    if (field === 'before') {
      setBeforeImage(file);
      setBeforePreview(preview);
    } else {
      setAfterImage(file);
      setAfterPreview(preview);
    }
  }, []);

  const handleAnalyze = useCallback(async () => {
    const errors = {};
    if (!beforeImage) errors.before = 'Before image is required.';
    if (!afterImage) errors.after = 'After image is required.';

    if (Object.keys(errors).length) {
      setFieldErrors(errors);
      setError('Please upload both before and after images.');
      return;
    }

    setLoading(true);
    setError('');
    setResults(null);

    try {
      const formData = new FormData();
      formData.append('before_image', beforeImage);
      formData.append('after_image', afterImage);

      const data = await analyzeNose(formData);

      if (!data?.success) {
        throw new Error(data?.message || 'Analysis failed. Please try again.');
      }

      setResults(data);
    } catch (err) {
      const message = err.response?.data?.message || err.message || 'Unable to analyze images.';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [beforeImage, afterImage]);

  return {
    beforePreview,
    afterPreview,
    fieldErrors,
    error,
    loading,
    results,
    handleImageChange,
    handleAnalyze,
  };
}
